import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import SimpleBar from 'simplebar-react';
import { ReactComponent as SettingSvg } from '../../assets/svg/setting.svg';
import { ReactComponent as TrashSvg } from '../../assets/svg/trash.svg';
import { getGradingSystems } from '../../services/GradeService';
import {
    deleteGradeScheme,
    getGradeSchemes,
    getGradeSchemeTemplates,
} from '../../services/GradeSchemeService';
import { deleteRubric, getRubrics, getRubricTemplates } from '../../services/RubricService';
import ModalWidget from '../modals/ModalWidget';
import { useToast } from '../../contexts/ToastContext';
import { useColor } from '../../contexts/ColorContext';
import { GradeSchemeTemplate, RubricTemplate } from '../../types';
import {
    detectUserRegion,
    normalizeGradingName,
    sortByRegion,
} from '../../utils/gradingDefaults';
import NewGradingSystem from './NewGradingSystem';
import SelectGradingSystemItem from './SelectGradingSystemItem';

export interface GradingDefaultsPickerProps {
    variant?: 'classroom' | 'school';
    classroomId?: string;
    selectedGradingScheme: any;
    selectedRubric: any;
    onSchemeChange: (scheme: any) => void;
    onRubricChange: (rubric: any) => void;
    onSchemeAdded?: (scheme: any) => void;
    onRubricAdded?: (rubric: any) => void;
    initialSchemeId?: string | null;
    initialRubricId?: string | null;
    initialSchemeTemplateKey?: string | null;
    initialRubricTemplateKey?: string | null;
    canEditGradingScheme?: boolean;
    canEditRubric?: boolean;
    schoolDefaultSchemeId?: string | null;
    schoolDefaultRubricId?: string | null;
    collapsedSummary?: string | null;
}

interface PresetToggleCardProps {
    showPresets: boolean;
    onToggle: () => void;
}

export const PresetToggleCard: React.FC<PresetToggleCardProps> = ({ showPresets, onToggle }) => {
    const { colors } = useColor();
    return (
        <div
            className="card-widget d-flex flex-column justify-content-center align-items-center"
            style={{
                minWidth: '150px',
                borderRadius: '8px',
                padding: '12px 16px',
                backgroundColor: colors.white,
                border: '1px dashed #C8C2D2',
                cursor: 'pointer',
            }}
            onClick={onToggle}
        >
            <div className="font-xs font-bold text-center">
                {showPresets ? 'Hide presets' : 'Show presets'}
            </div>
            <Button
                variant="outline-secondary"
                className="rounded-pill font-xs mt-2 px-3 py-1"
                onClick={(event) => {
                    event.stopPropagation();
                    onToggle();
                }}
            >
                {showPresets ? 'Hide' : 'Show'}
            </Button>
        </div>
    );
};

const GradingDefaultsPicker: React.FC<GradingDefaultsPickerProps> = ({
    variant = 'classroom',
    classroomId,
    selectedGradingScheme,
    selectedRubric,
    onSchemeChange,
    onRubricChange,
    onSchemeAdded,
    onRubricAdded,
    initialSchemeId = null,
    initialRubricId = null,
    initialSchemeTemplateKey = null,
    initialRubricTemplateKey = null,
    canEditGradingScheme = true,
    canEditRubric = true,
    schoolDefaultSchemeId = null,
    schoolDefaultRubricId = null,
    collapsedSummary = null,
}) => {
    const { showError, showSuccess } = useToast();
    const isSchoolMode = variant === 'school';
    const [advancedOpen, setAdvancedOpen] = useState(isSchoolMode);

    const [gradingSchemes, setGradingSchemes] = useState<any[]>([]);
    const [rubrics, setRubrics] = useState<any[]>([]);
    const [gradeSchemeTemplates, setGradeSchemeTemplates] = useState<GradeSchemeTemplate[]>([]);
    const [rubricTemplates, setRubricTemplates] = useState<RubricTemplate[]>([]);
    const [schemeTemplatesLoaded, setSchemeTemplatesLoaded] = useState(false);
    const [rubricTemplatesLoaded, setRubricTemplatesLoaded] = useState(false);
    const [showSchemePresets, setShowSchemePresets] = useState(false);
    const [showRubricPresets, setShowRubricPresets] = useState(false);
    const [schemeToDelete, setSchemeToDelete] = useState<any>(null);
    const [rubricToDelete, setRubricToDelete] = useState<any>(null);
    const [deletingScheme, setDeletingScheme] = useState(false);
    const [deletingRubric, setDeletingRubric] = useState(false);

    const retrieveGradingSchemes = useCallback(async () => {
        try {
            const res = await getGradeSchemes();
            if (res.data) setGradingSchemes(res.data);
        } catch {
            try {
                const res = await getGradingSystems();
                if (res.status === 'success') setGradingSchemes(res.data);
            } catch {
                /* ignore */
            }
        }
    }, []);

    const retrieveRubrics = useCallback(async () => {
        try {
            const res = await getRubrics();
            if (res.data) setRubrics(res.data);
        } catch {
            /* ignore */
        }
    }, []);

    const loadSchemeTemplates = useCallback(async () => {
        if (schemeTemplatesLoaded) return;
        try {
            const res = await getGradeSchemeTemplates();
            const userRegion = detectUserRegion();
            setGradeSchemeTemplates(sortByRegion(res.data || [], userRegion));
            setSchemeTemplatesLoaded(true);
        } catch {
            setGradeSchemeTemplates([]);
            setSchemeTemplatesLoaded(true);
        }
    }, [schemeTemplatesLoaded]);

    const loadRubricTemplates = useCallback(async () => {
        if (rubricTemplatesLoaded) return;
        try {
            const res = await getRubricTemplates();
            const userRegion = detectUserRegion();
            setRubricTemplates(sortByRegion(res.data || [], userRegion));
            setRubricTemplatesLoaded(true);
        } catch {
            setRubricTemplates([]);
            setRubricTemplatesLoaded(true);
        }
    }, [rubricTemplatesLoaded]);

    useEffect(() => {
        retrieveGradingSchemes();
        retrieveRubrics();
    }, [retrieveGradingSchemes, retrieveRubrics]);

    useEffect(() => {
        if (!schoolDefaultSchemeId || selectedGradingScheme) return;
        const match = gradingSchemes.find(
            (s) => (s.id ?? s.uuid) === schoolDefaultSchemeId,
        );
        if (match) onSchemeChange(match);
    }, [gradingSchemes, schoolDefaultSchemeId, selectedGradingScheme, onSchemeChange]);

    useEffect(() => {
        if (!schoolDefaultRubricId || selectedRubric) return;
        const match = rubrics.find(
            (r) => (r.id ?? r.uuid) === schoolDefaultRubricId,
        );
        if (match) onRubricChange(match);
    }, [rubrics, schoolDefaultRubricId, selectedRubric, onRubricChange]);

    const handleToggleAdvanced = async () => {
        const next = !advancedOpen;
        setAdvancedOpen(next);
        if (next) {
            if (gradingSchemes.length === 0) {
                await loadSchemeTemplates();
                setShowSchemePresets(true);
            }
            if (rubrics.length === 0) {
                await loadRubricTemplates();
                setShowRubricPresets(true);
            }
        }
    };

    const displayRubrics = useMemo(() => {
        const seen = new Set<string>();
        const filtered = rubrics.filter((r: any) => {
            if (r.classroom_id && r.classroom_id !== classroomId) return false;
            const name = normalizeGradingName(r.name);
            if (seen.has(name)) return false;
            seen.add(name);
            return true;
        });
        if (initialRubricId) {
            filtered.sort((a: any, b: any) => (
                a.id === initialRubricId ? -1 : b.id === initialRubricId ? 1 : 0
            ));
        }
        return filtered;
    }, [rubrics, classroomId, initialRubricId]);

    const sortedGradingSchemes = useMemo(() => {
        if (!initialSchemeId || !gradingSchemes.length) return gradingSchemes;
        return [...gradingSchemes].sort((a: any, b: any) => (
            (a.uuid ?? a.id) === initialSchemeId ? -1 : (b.uuid ?? b.id) === initialSchemeId ? 1 : 0
        ));
    }, [gradingSchemes, initialSchemeId]);

    const displaySchemeTemplates = useMemo(() => {
        if (!showSchemePresets && gradingSchemes.length > 0) return [];
        if (!gradeSchemeTemplates.length) return gradeSchemeTemplates;
        const selKey = initialSchemeTemplateKey;
        if (!selKey) return gradeSchemeTemplates;
        return [...gradeSchemeTemplates].sort((a, b) => (
            a.key === selKey ? -1 : b.key === selKey ? 1 : 0
        ));
    }, [gradeSchemeTemplates, initialSchemeTemplateKey, showSchemePresets, gradingSchemes.length]);

    const displayRubricTemplates = useMemo(() => {
        if (!showRubricPresets && rubrics.length > 0) return [];
        if (!rubricTemplates.length) return rubricTemplates;
        const selKey = initialRubricTemplateKey;
        if (!selKey) return rubricTemplates;
        return [...rubricTemplates].sort((a, b) => (
            a.key === selKey ? -1 : b.key === selKey ? 1 : 0
        ));
    }, [rubricTemplates, initialRubricTemplateKey, showRubricPresets, rubrics.length]);

    const handleSchemeAdded = async (scheme: any) => {
        await retrieveGradingSchemes();
        onSchemeChange(scheme);
        onSchemeAdded?.(scheme);
    };

    const handleRubricAdded = async (rubric: any) => {
        await retrieveRubrics();
        onRubricChange(rubric);
        onRubricAdded?.(rubric);
    };

    const handleConfirmDeleteScheme = async () => {
        if (!schemeToDelete) return;
        const schemeId = schemeToDelete.uuid ?? schemeToDelete.id;
        if (!schemeId) return;
        setDeletingScheme(true);
        try {
            await deleteGradeScheme(schemeId);
            await retrieveGradingSchemes();
            if (
                selectedGradingScheme
                && (selectedGradingScheme.uuid ?? selectedGradingScheme.id) === schemeId
            ) {
                onSchemeChange(null);
            }
            showSuccess('Grading scheme deleted.');
            setSchemeToDelete(null);
        } catch (err: any) {
            showError(err?.response?.data?.detail ?? 'Unable to delete this grading scheme.');
        } finally {
            setDeletingScheme(false);
        }
    };

    const handleConfirmDeleteRubric = async () => {
        if (!rubricToDelete) return;
        const rubricId = rubricToDelete.id;
        if (!rubricId) return;
        setDeletingRubric(true);
        try {
            await deleteRubric(rubricId);
            await retrieveRubrics();
            if (selectedRubric?.id === rubricId) {
                onRubricChange(null);
            }
            showSuccess('Rubric deleted.');
            setRubricToDelete(null);
        } catch (err: any) {
            showError(err?.response?.data?.detail ?? 'Unable to delete this rubric.');
        } finally {
            setDeletingRubric(false);
        }
    };

    const pickerBody = (
        <>
            <div className="mt-3 font-xs font-bold">Grading Scheme (click item to select)</div>
            {!canEditGradingScheme && (
                <div className="font-xs text-muted mt-1">
                    Your school settings restrict editing grading schemes.
                </div>
            )}
            <SimpleBar className="mt-2">
                <div className="d-flex">
                    {canEditGradingScheme && (
                        <>
                            <NewGradingSystem
                                existingNames={gradingSchemes.map((s) => s.name)}
                                templateNames={gradeSchemeTemplates.map((t) => t.name)}
                                onAdded={handleSchemeAdded}
                            />
                            <div className="mx-2" />
                        </>
                    )}
                    {sortedGradingSchemes.map((scheme, index) => (
                        <React.Fragment key={scheme.uuid ?? scheme.id ?? index}>
                            <SelectGradingSystemItem
                                gradingSystem={scheme}
                                itemKey={scheme.uuid ?? scheme.id}
                                selected={
                                    selectedGradingScheme
                                        ? (scheme.uuid ?? scheme.id)
                                            === (selectedGradingScheme.uuid ?? selectedGradingScheme.id)
                                        : false
                                }
                                onClick={() => onSchemeChange(scheme)}
                                onAdded={handleSchemeAdded}
                                onDelete={canEditGradingScheme ? setSchemeToDelete : undefined}
                                existingNames={gradingSchemes.map((s) => s.name)}
                                templateNames={gradeSchemeTemplates.map((t) => t.name)}
                            />
                            <div className="mx-2" />
                        </React.Fragment>
                    ))}
                    {displaySchemeTemplates.map((tpl) => (
                        <React.Fragment key={tpl.key}>
                            <SelectGradingSystemItem
                                gradingSystem={{
                                    ...tpl,
                                    uuid: tpl.key,
                                    id: tpl.key,
                                    assessments: [],
                                    is_not_school_owned: true,
                                } as any}
                                itemKey={`tpl-scheme-${tpl.key}`}
                                selected={selectedGradingScheme?.key === tpl.key}
                                onClick={() => onSchemeChange({ ...tpl, uuid: undefined, id: undefined })}
                                onAdded={handleSchemeAdded}
                                existingNames={gradingSchemes.map((s) => s.name)}
                                templateNames={gradeSchemeTemplates.map((t) => t.name)}
                            />
                            <div className="mx-2" />
                        </React.Fragment>
                    ))}
                    {gradingSchemes.length > 0 && canEditGradingScheme && (
                        <>
                            <PresetToggleCard
                                showPresets={showSchemePresets}
                                onToggle={async () => {
                                    if (!showSchemePresets) {
                                        await loadSchemeTemplates();
                                    }
                                    setShowSchemePresets((prev) => !prev);
                                }}
                            />
                            <div className="mx-2" />
                        </>
                    )}
                </div>
            </SimpleBar>

            <div className="mt-4 font-xs font-bold">Rubric (click item to select)</div>
            {!canEditRubric && (
                <div className="font-xs text-muted mt-1">
                    Your school settings restrict editing rubrics.
                </div>
            )}
            <SimpleBar className="mt-2">
                <div className="d-flex">
                    {canEditRubric && (
                        <>
                            <NewGradingSystem
                                mode="rubric"
                                existingNames={rubrics.map((r: any) => r.name)}
                                templateNames={rubricTemplates.map((t) => t.name)}
                                onAdded={handleRubricAdded}
                            />
                            <div className="mx-2" />
                        </>
                    )}
                    {displayRubrics.map((rubric, index) => (
                        <React.Fragment key={rubric.id ?? index}>
                            <SelectGradingSystemItem
                                gradingSystem={rubric as any}
                                itemKey={rubric.id}
                                mode="rubric"
                                selected={
                                    selectedRubric
                                        ? (rubric.id ?? rubric.uuid) === (selectedRubric.id ?? selectedRubric.uuid)
                                        : false
                                }
                                onClick={() => onRubricChange(rubric)}
                                onAdded={handleRubricAdded}
                                onDelete={canEditRubric ? setRubricToDelete : undefined}
                                existingNames={rubrics.map((r: any) => r.name)}
                                templateNames={rubricTemplates.map((t) => t.name)}
                            />
                            <div className="mx-2" />
                        </React.Fragment>
                    ))}
                    {displayRubricTemplates.map((tpl) => (
                        <React.Fragment key={tpl.key}>
                            <SelectGradingSystemItem
                                gradingSystem={{
                                    ...tpl,
                                    uuid: tpl.key,
                                    id: tpl.key,
                                    assessments: [],
                                    is_not_school_owned: true,
                                } as any}
                                itemKey={`tpl-rubric-${tpl.key}`}
                                mode="rubric"
                                selected={selectedRubric?.key === tpl.key}
                                onClick={() => onRubricChange({ ...tpl, id: undefined })}
                                onAdded={handleRubricAdded}
                                existingNames={rubrics.map((r: any) => r.name)}
                                templateNames={rubricTemplates.map((t) => t.name)}
                            />
                            <div className="mx-2" />
                        </React.Fragment>
                    ))}
                    {rubrics.length > 0 && canEditRubric && (
                        <>
                            <PresetToggleCard
                                showPresets={showRubricPresets}
                                onToggle={async () => {
                                    if (!showRubricPresets) {
                                        await loadRubricTemplates();
                                    }
                                    setShowRubricPresets((prev) => !prev);
                                }}
                            />
                            <div className="mx-2" />
                        </>
                    )}
                </div>
            </SimpleBar>
        </>
    );

    const deleteModals = (
        <>
            {schemeToDelete && (
                <ModalWidget
                    show
                    navigateBack={false}
                    onHide={() => setSchemeToDelete(null)}
                    title={`Delete "${schemeToDelete.name}"?`}
                    description="This cannot be undone. Change any school, classroom, or template defaults first."
                    actionTitle={deletingScheme ? 'Deleting…' : 'Delete grading scheme'}
                    actionIcon={TrashSvg}
                    actionButtonColor="#FF3633"
                    actionLoading={deletingScheme}
                    onAction={handleConfirmDeleteScheme}
                />
            )}
            {rubricToDelete && (
                <ModalWidget
                    show
                    navigateBack={false}
                    onHide={() => setRubricToDelete(null)}
                    title={`Delete "${rubricToDelete.name}"?`}
                    description="This cannot be undone. Change any school, classroom, or template defaults first."
                    actionTitle={deletingRubric ? 'Deleting…' : 'Delete rubric'}
                    actionIcon={TrashSvg}
                    actionButtonColor="#FF3633"
                    actionLoading={deletingRubric}
                    onAction={handleConfirmDeleteRubric}
                />
            )}
        </>
    );

    if (isSchoolMode) {
        return (
            <div data-testid="grading-defaults-picker">
                {pickerBody}
                {deleteModals}
            </div>
        );
    }

    return (
        <div data-testid="grading-defaults-picker" className="mt-2">
            {!advancedOpen && (
                <div className="d-flex align-items-center flex-wrap gap-2 mt-2">
                    <Button
                        variant="outline-secondary"
                        className="rounded-pill d-flex align-items-center gap-2 font-xs px-3 py-1"
                        onClick={handleToggleAdvanced}
                    >
                        <SettingSvg width={14} height={14} />
                        Advanced
                    </Button>
                    {collapsedSummary && (
                        <span className="font-xs text-muted">{collapsedSummary}</span>
                    )}
                </div>
            )}
            {advancedOpen && (
                <>
                    <div className="d-flex align-items-center mt-2 mb-1">
                        <Button
                            variant="outline-secondary"
                            className="rounded-pill d-flex align-items-center gap-2 font-xs px-3 py-1"
                            onClick={handleToggleAdvanced}
                        >
                            <SettingSvg width={14} height={14} />
                            Advanced
                        </Button>
                    </div>
                    {pickerBody}
                </>
            )}
            {deleteModals}
        </div>
    );
};

export default GradingDefaultsPicker;
